/*
  Options shared by the browser, Node and Edge Sentry clients.

  These live in one place so the three `Sentry.init()` entry points can't drift:
  a filter that only lands in one runtime silently stops working for the others,
  which is hard to notice because the symptom is "quota is gone", not an error.

  The event counts quoted below are production, 30 days, measured before these
  filters were added — they're here so the next person can tell whether a rule
  is still earning its place.
*/

/*
  Environments that are allowed to report. `.env` is committed and carries the
  production DSN, so without this gate every developer's machine reports into
  the production project (~200 events/30d under an `environment:development`
  tag, plus whatever CI produces). Sentry treats a missing DSN as "disabled",
  so anything not listed here is a no-op.

  Where the build-time value comes from, since this is easy to get wrong:
  `next build` runs on the CI *runner* (`build:ci:marketplace`, the "Run PNPM"
  step), not inside Docker. It reads the job-level `env:` of
  .github/workflows/reusable.docker-build.yaml, where NEXT_PUBLIC_ENVIRONMENT
  is set from the `environment` input — a `required: true` input, so it cannot
  silently arrive empty. The later docker step only copies the finished `.next`
  into the image; apps/marketplace/marketplace.Dockerfile runs no build at all.
  (apps/marketplace/Dockerfile does declare build ARGs, but nothing references
  that file — it is dead, and reading it will mislead you.)

  The same spellings are used at runtime in
  infrastructure/marketplace/<env>/values.yaml, which is what server and edge
  read. Both sides must stay in step or one runtime will stop reporting.
*/
const REPORTING_ENVIRONMENTS = ['production', 'staging', 'test', 'ephemeral'];

export const environment = process.env.NEXT_PUBLIC_ENVIRONMENT;

export const dsn = REPORTING_ENVIRONMENTS.includes(environment ?? '')
  ? process.env.NEXT_PUBLIC_SENTRY_DSN
  : undefined;

/*
  Two variables, one per side of the build boundary. This looks redundant and
  is not.

  Next.js inlines every NEXT_PUBLIC_* variable that is *present at build time*
  into all bundles — server and edge included, not just the browser. (The same
  gotcha is documented in src/instrumentation.ts, which is why the GCP secrets
  there drop the NEXT_PUBLIC_ prefix.) sentry.server.config.js is pulled in via
  `await import()` from instrumentation.ts and so goes through the server
  compiler, so a NEXT_PUBLIC_ rate present at build gets frozen into the server
  bundle and the pod env is never consulted again.

  So:
    - SENTRY_TRACES_SAMPLE_RATE (no prefix) is never inlined and survives to
      pod runtime. Server and edge read it from
      infrastructure/marketplace/<env>/values.yaml, which keeps that knob live
      — a rate can still be raised during an incident without a rebuild.
    - NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE is inlined at build from the
      `sentry-traces-sample-rate` input of reusable.docker-build.yaml. In the
      browser the un-prefixed lookup resolves to undefined, so this is what
      applies.

  Until that build input existed the browser value was simply absent, so
  browser tracing had been silently dead — Sentry recorded zero
  pageload/navigation transactions over 30 days. The browser rate is
  deliberately lower than the server rate in prd, where the traffic is.

  `parseFloat(undefined)` is `NaN`, and a `NaN` rate disables tracing without
  complaining. The `|| 0` makes that outcome explicit rather than accidental.
*/
export const tracesSampleRate =
  parseFloat(
    process.env.SENTRY_TRACES_SAMPLE_RATE ??
      process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE ??
      ''
  ) || 0;

/*
  Errors are still captured in full in the environments we actually watch. PR
  previews are the exception: every open PR gets its own deployment, they all
  report into the same project, and between them they produced ~6k events/30d
  that nobody triages.
*/
export const sampleRate = environment === 'ephemeral' ? 0.1 : 1;

/*
  Browser-only. Server-side these strings would mean something real (a genuine
  upstream connectivity failure), so this list is deliberately not applied to
  the Node or Edge clients.
*/
export const ignoreErrors = [
  /*
    Generic browser network failures — tab closed mid-request, connection
    dropped, request cancelled by an ad-blocker, device went offline. ~67k
    events/30d, about half of all production errors, spread across eight
    separate issues. None of them are actionable and none have a stack trace
    that points anywhere useful.
  */
  /*
    Anchored regexes rather than bare strings wherever the phrase is short
    enough to appear inside a real message. Sentry matches string entries as
    substrings, so an unanchored `Failed to fetch` also swallows our own
    "Failed to fetch review stats" (useReviews.ts), and `cancelled` /
    `Load failed` would swallow "Order cancelled" / "Image load failed".

    Anchoring is safe because Sentry tests the exception value on its own as
    well as the combined "Type: value" form — `TypeError: Failed to fetch`
    still matches on its value.
  */
  /^Failed to fetch$/, // Chrome
  // Not anchored: Firefox appends a trailing period in some versions.
  'NetworkError when attempting to fetch resource', // Firefox
  /^Load failed$/, // Safari
  'The network connection was lost', // iOS Safari
  /^cancelled$/i, // iOS Safari
  'AbortError',
  /*
    Fires when a resize handler doesn't settle within one animation frame. The
    browser recovers on the next frame and the user never sees anything.
  */
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Next.js throws this when one navigation interrupts another.
  'Cancel rendering route',
];

/*
  Browser-only. Matched against the URL of the frame the event came from, so it
  filters by *who wrote the code*, not by what the error said.

  Only useful for stacks that contain at least one frame with a real filename.
  `_getLastValidUrl` walks the frames from the top and skips `<anonymous>` and
  `[native code]` entirely, so a stack made up only of those yields no URL and
  no rule here can ever match it — see `beforeSend` below for that case.
*/
export const denyUrls = [
  /*
    Extensions and userscripts injecting into the page: MetaMask (`inpage`),
    `injectScriptAdjust`, `frame_ant`, `genies`, `content/activeContent`, and a
    Tampermonkey script literally named "Unity Asset Store - Publisher Email
    Cycle Debug.user". ~25k events/30d across ~14 issues, none of it our code
    and none of it something we can fix.
  */
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /^webkit-masked-url:/i,
  /\.user\.js$/i,
];

/*
  Filenames that carry no attribution: a frame with one of these cannot be
  traced back to any file, ours or anyone else's.
*/
const OPAQUE_FILENAMES = new Set(['<anonymous>', '[native code]']);

/*
  Browser-only, and the counterpart to `denyUrls` above.

  Scripts injected with no source URL at all — `ReferenceError: e is not
  defined` at `c(<anonymous>)`, ~7k events/30d — produce stacks whose frames
  are all `<anonymous>`. `denyUrls` provably cannot reach them: Sentry's
  `_getLastValidUrl` skips exactly those filenames while hunting for a URL,
  returns `null` for an all-anonymous stack, and `_isDeniedUrl` then keeps the
  event. So this has to happen in `beforeSend`, which runs after the inbound
  filters and sees the whole event.

  Deliberately conservative, because dropping an event is silent and
  irreversible:
    - an event with no frames at all is kept, so `captureMessage` and any
      exception Sentry could not unwind still report;
    - a single frame with a real filename anywhere in the stack keeps the whole
      event, including a chained exception whose cause is ours;
    - only filenames explicitly in the set count as opaque — a frame with no
      `filename` property at all is not enough to trigger a drop.

  The durable fix is `thirdPartyErrorFilterIntegration`, which uses build-time
  module metadata to keep only errors originating in our own bundles. It needs
  @sentry/nextjs v8+, so it is a follow-up to the SDK upgrade.
*/
type StackFrame = { filename?: string };
type ExceptionValue = { stacktrace?: { frames?: StackFrame[] } };
type SentryEvent = { exception?: { values?: ExceptionValue[] } };

export const hasOnlyOpaqueFrames = (event: SentryEvent): boolean => {
  const frames = (event.exception?.values ?? []).flatMap(
    (value) => value.stacktrace?.frames ?? []
  );

  return (
    frames.length > 0 &&
    frames.every(
      (frame) =>
        frame.filename !== undefined && OPAQUE_FILENAMES.has(frame.filename)
    )
  );
};

export const beforeSend = <T extends SentryEvent>(event: T): T | null =>
  hasOnlyOpaqueFrames(event) ? null : event;

type SamplingContext = {
  transactionContext?: { name?: string; op?: string };
  /** Set when the incoming request carried a `sentry-trace` header. */
  parentSampled?: boolean;
};

/*
  Transactions dropped outright. Between them these two account for ~65% of
  every transaction this project ingests, and neither tells us anything we
  can't already see elsewhere.
*/
const UNSAMPLED_TRANSACTION_NAMES = new Set([
  /*
    ~156M/30d — ~46% of the project's entire transaction volume, and the single
    largest line item on the usage dashboard.

    The middleware chain fetches this over HTTP up to three times per request:
    localize-routes and addCspHeader each call it via `isCompassUIPage`, and
    landingPageRouter calls it directly. All three end up reading one Redis key
    with a 30-day TTL, so the traces carry no diagnostic value whatsoever.

    Note this only stops us *paying* for the traces. The triple fetch itself is
    still worth deduplicating per request — see src/lib/util/routes.ts. It most
    likely exists because middleware runs on the Edge runtime and can't reach
    the Redis client directly, so removing it is a larger change than it looks.
  */
  'GET /api/cms/landing-page-slugs',
]);

const UNSAMPLED_TRANSACTION_OPS = new Set([
  /*
    ~64M/30d, ~19% of volume — one per matched request. The work it covers is
    already described by the page or route transaction that follows it.
  */
  'middleware.nextjs',
]);

/*
  Takes precedence over `tracesSampleRate`, which is why the init files pass
  this instead. Everything that isn't explicitly dropped falls through to the
  configured rate, so per-environment tuning still happens in the helm values.

  Order matters. The exclusions are checked first so that an inbound sampled
  trace can't resurrect the two transactions above — otherwise a single sampled
  page request would drag its three landing-page-slugs calls in with it, which
  is most of what this sampler exists to prevent.
*/
export const tracesSampler = (
  samplingContext: SamplingContext
): number | boolean => {
  const { name, op } = samplingContext.transactionContext ?? {};

  if (
    (name && UNSAMPLED_TRANSACTION_NAMES.has(name)) ||
    (op && UNSAMPLED_TRANSACTION_OPS.has(op))
  ) {
    return 0;
  }

  /*
    Defining a `tracesSampler` at all replaces the SDK's built-in parentSampled
    inheritance, so it has to be reapplied by hand. Without this a request that
    the browser already sampled gets rolled for again on the server, which both
    breaks traces in half and produces orphaned server transactions with no
    parent.
  */
  return samplingContext.parentSampled ?? tracesSampleRate;
};
